<script context="module" lang="ts">
  import { writable, type Writable } from 'svelte/store';
  import type { Api_PurchasedProductCoaching } from '$lib/types/api/graphql';

  let coaching: Writable<Api_PurchasedProductCoaching | null> = writable(null);

  let isVisible = writable(false);

  export function openIntakeQuestionsModal() {
    isVisible.set(true);
  }

  export function setIntakeQuestionsModal(c: Api_PurchasedProductCoaching) {
    coaching.set(c);
  }

  export function removeIntakeQuestionsModal() {
    isVisible.set(false);
    coaching.set(null);
  }
</script>

<script lang="ts">
  import { gql, mutate } from '$lib/apollo';
  import Dialog from '$mds/Dialogs/Dialog.svelte';
  import FormControl from '$components/FormControl.svelte';
  import { IRK_API_URL } from '$lib/constants';
  import { page } from '$app/stores';
  import { GET_COACHING_SESSION } from '$routes/(nav)/account/session/[coachingId]/[coachingSessionId]/gql';
  import { GET_COACHING } from '$routes/(nav)/account/session/[coachingId]/gql';

  let isSubmiting = false;
  let answers = {};
  async function handleIntakeAanswersSubmit() {
    try {
      isSubmiting = true;

      let refetchQueries = $page.params?.coachingSessionId
        ? [
            {
              query: GET_COACHING_SESSION,
              variables: { id: $page.params.coachingSessionId }
            }
          ]
        : $page.params?.coachingId
          ? [
              {
                query: GET_COACHING,
                variables: { id: $page.params.coachingId }
              }
            ]
          : [];

      const answerEntries = Object.entries(answers);
      const promises = answerEntries.map(([id, answer], idx) =>
        mutate({
          mutation: gql`
            mutation CoachingIntakeQuestionAnswer($input: CoachingIntakeQuestionAnswerInput!) {
              coachingIntakeQuestionAnswer(input: $input) {
                errors {
                  ... on Error {
                    code
                    message
                  }
                }
                result {
                  id
                }
              }
            }
          `,
          variables: { input: { id, answer } },
          refetchQueries: idx === answerEntries.length - 1 ? refetchQueries : [], // Refetch only on the last one
          awaitRefetchQueries: true
        })
      );

      await Promise.all(promises);
      removeIntakeQuestionsModal();
    } finally {
      isSubmiting = false;
    }
  }
</script>

<Dialog
  class="!text-left"
  isOpen={$isVisible}
  scrollerClass="-mx-1 px-1 py-1"
  title="Jump start your Live Session with {$coaching?.host?.name}!"
  allowClickOutside={false}
  allowCloseOnNavigation={false}
  primaryAction={{
    label: 'Submit',
    width: 'full',
    action: handleIntakeAanswersSubmit,
    disabled: Object.values(answers).some(a => !a),
    loading: isSubmiting
  }}
  secondaryAction={{
    label: 'Close without answering',
    width: 'full',
    action: removeIntakeQuestionsModal
  }}
  on:closed={removeIntakeQuestionsModal}
>
  <p class="text-ondark-subtle">
    {$coaching?.host?.name} has put together a few questions for you. Answering these before your Session will help guide
    your experience.
  </p>

  {#if $coaching}
    {#each $coaching.intakeQuestions as intakeQuestion (intakeQuestion.id)}
      <div class="mt-4">
        <FormControl
          label={intakeQuestion.question}
          placeholder="Add your answer"
          value={intakeQuestion?.answer ?? ''}
          on:input={e => (answers[intakeQuestion.id] = e.target.value)}
        />
      </div>
    {/each}
  {/if}
</Dialog>
